import { computed, inject } from 'vue';
import type { ComputedRef } from 'vue';
import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import type { MatchFunction, ParamData } from 'path-to-regexp';
import type { Features } from '~/provides/features.types';
import {
    faBadgePercent,
    faBookAtlas,
    faBuildingFlag,
    faCalendarCirclePlus,
    faCircleInfo,
    faCircleNodes,
    faCircleUser,
    faFolderArrowDown,
    faContactBook,
    faCreditCard,
    faEye,
    faFingerprint,
    faFlagCheckered,
    faGrid2Plus,
    faKeySkeletonLeftRight,
    faWandMagicSparkles,
    faListUl,
    faLockKeyhole,
    faMoneyCheckDollarPen,
    faPhone,
    faPhoneOffice,
    faPuzzlePiece,
    faRectangleHistoryCirclePlus,
    faRectangleHistoryCircleUser,
    faRightToBracket,
    faShoppingCart,
    faSparkle,
    faUserPlus,
    faUsers,
    faPhoneArrowRight,
    faCalendarAlt,
    faFolderArrowRight,
    faCashRegister,
    faWaveformLines,
    faInbox,
} from '@fortawesome/pro-solid-svg-icons';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { match } from 'path-to-regexp';
import { faHubspot, faShopify } from '@fortawesome/free-brands-svg-icons';

export interface SettingsLink {
    href: string;
    label: string;
    icon: IconDefinition;
    pathMatcher?: MatchFunction<ParamData>;
}

export interface SettingsSection {
    label: string;
    links: SettingsLink[];
}

export function settingsLinks(): ComputedRef<SettingsSection[]> {
    const isTeamOwner = Spark.ownsTeam;
    const isAdminOrOwner = Spark.isAdminOrOwner;
    const isServiceRepresentative = Spark.isServiceRepresentative;

    const user = Spark.state.user;

    const teamId = Spark.state.currentTeam.id;
    const parentId = Spark.state.currentTeam.parent_id;
    const isBillable = !parentId || Spark.state.currentTeam.is_billable;
    const teamStatus = Spark.state.currentTeam.status;

    const FEATURES = inject<Features>('FEATURES')!;

    const { onCorporate, roleOnCurrentGroup, currentGroup, roleOnCurrentOrgUnit, currentOrgUnit } = useCurrentGroup();

    return computed(() => {
        const yourSettings: SettingsSection = {
            label: 'User Settings',
            links: [
                {
                    href: `/settings#/profile`,
                    label: 'Profile',
                    icon: faCircleUser,
                },
            ],
        };

        yourSettings.links.push({
            href: `/settings#/teams`,
            label: 'Teams',
            icon: faUsers,
        });

        if (!user?.isUsingSingleSignOn) {
            yourSettings.links.push({
                href: `/settings#/invites`,
                label: 'Invites',
                icon: faUserPlus,
            });

            yourSettings.links.push({
                href: `/settings#/security`,
                label: 'Security',
                icon: faLockKeyhole,
            });
        }

        if (onCorporate() && !user?.isUsingSingleSignOn) {
            yourSettings.links.push({
                href: `/settings#/api`,
                label: 'API Tokens',
                icon: faKeySkeletonLeftRight,
            });
        }

        const allLinks: SettingsSection[] = [yourSettings];

        if (onCorporate()) {
            const teamSettings: SettingsSection = {
                label: 'Team Settings',
                links: [],
            };

            teamSettings.links.push({
                href: `/settings/teams/${teamId}#/owner`,
                label: 'Team Profile',
                icon: faCircleUser,
            });

            if (isTeamOwner && !parentId) {
                if (FEATURES.single_sign_on) {
                    teamSettings.links.push({
                        href: `/settings/teams/${teamId}#/sso-settings`,
                        label: 'Account SSO',
                        icon: faRightToBracket,
                    });
                }
            }

            if (!user?.isUsingSingleSignOn) {
                teamSettings.links.push({
                    href: `/settings/teams/${teamId}#/membership`,
                    label: isAdminOrOwner ? 'Users' : 'Membership',
                    icon: faUsers,
                });
            }

            if (!isServiceRepresentative) {
                teamSettings.links.push({
                    href: `/settings/teams/${teamId}#/phone-numbers`,
                    label: 'Phone Numbers',
                    icon: faPhone,
                });
            }

            if (!isServiceRepresentative) {
                teamSettings.links.push({
                    href: `/settings/teams/${teamId}#/phone-number-pools`,
                    label: 'Phone Pools',
                    icon: faPhoneOffice,
                });
            }

            teamSettings.links.push({
                href: `/settings/teams/${teamId}#/customattributes`,
                label: 'Custom Attributes',
                icon: faPuzzlePiece,
            });

            teamSettings.links.push({
                href: `/settings/teams/${teamId}#/contacts-exports`,
                label: 'Contacts Exports',
                icon: faContactBook,
            });

            if (!isServiceRepresentative) {
                teamSettings.links.push({
                    href: '/settings/integrations/promotions',
                    label: 'Promotions',
                    icon: faBadgePercent,
                });
            }

            allLinks.push(teamSettings);

            const isImpersonatorOrPlatformAdmin = Spark.isPlatformAdmin || Spark.isImpersonating;

            const showAiBlastVetting = FEATURES.contact_groups;
            const showAiConfiguration = isAdminOrOwner;
            const showIntelligentInbox =
                (FEATURES.intelligent_inbox && isAdminOrOwner) || isImpersonatorOrPlatformAdmin;

            if (showAiBlastVetting || showAiConfiguration || showIntelligentInbox) {
                const aiAgents: SettingsSection = {
                    label: 'AI Agents',
                    links: [],
                };

                if (showAiBlastVetting) {
                    aiAgents.links.push({
                        href: `/settings/teams/${teamId}#/ai-blast-vetting`,
                        label: 'AI Blast Vetting',
                        icon: faSparkle,
                    });
                }

                if (showAiConfiguration) {
                    aiAgents.links.push({
                        href: `/settings/teams/${teamId}#/ai-configuration`,
                        label: 'AI Configuration',
                        icon: faWandMagicSparkles,
                    });
                }

                if (showIntelligentInbox) {
                    aiAgents.links.push({
                        href: `/settings/teams/${teamId}#/intelligent-inbox`,
                        label: 'Intelligent Inbox',
                        icon: faInbox,
                    });
                }

                allLinks.push(aiAgents);
            }
        }

        if (onCorporate() && FEATURES.contact_groups) {
            const franchiseHub: SettingsSection = {
                label: 'Franchise Hub',
                links: [
                    {
                        href: `/settings/teams/${teamId}#/analytics-goals`,
                        label: 'Analytics Goals',
                        icon: faFlagCheckered,
                    },
                    {
                        href: `/settings/teams/${teamId}#/message-standards`,
                        label: 'Brand Standards',
                        icon: faListUl,
                    },
                    {
                        href: `/settings/teams/${teamId}#/org-units`,
                        label: 'OUs',
                        icon: faBuildingFlag,
                        pathMatcher: match(['/settings/teams/:teamId#/org-units{/*path}']),
                    },
                    {
                        href: `/settings/teams/${teamId}#/groups`,
                        label: 'Groups',
                        icon: faGrid2Plus,
                    },
                    {
                        href: `/settings/teams/${teamId}#/group-collections`,
                        label: 'Collections',
                        icon: faRectangleHistoryCirclePlus,
                    },
                ],
            };

            franchiseHub.links.push({
                href: `/settings/teams/${teamId}#/cost-visibility`,
                label: 'Cost Visibility',
                icon: faEye,
            });

            allLinks.push(franchiseHub);
        }

        if (onCorporate() && isAdminOrOwner) {
            const integrations: SettingsSection = {
                label: 'Integrations',
                links: [
                    {
                        href: '/settings/relay',
                        label: 'Voxie Relay',
                        icon: faCircleNodes,
                        pathMatcher: match(['/settings/relay', '/settings/relay/:integration']),
                    },
                ],
            };

            if (FEATURES.acuity_integration && FEATURES.contact_groups) {
                integrations.links.push({
                    href: '/settings/integrations#/acuity',
                    label: 'Acuity',
                    icon: faCalendarAlt,
                });
            }

            if (FEATURES.api_destinations) {
                integrations.links.push({
                    href: '/settings/integrations#/auth-configs',
                    label: 'API Auth',
                    icon: faFingerprint,
                });

                integrations.links.push({
                    href: '/settings/integrations#/api-destinations',
                    label: 'API Destinations',
                    icon: faBookAtlas,
                });
            }

            if (FEATURES.calendly_integration && FEATURES.contact_groups) {
                integrations.links.push({
                    href: '/settings/integrations#/calendly',
                    label: 'Calendly',
                    icon: faCalendarCirclePlus,
                });
            }

            integrations.links.push({
                href: '/settings/integrations#/contact-collector',
                label: 'Contact Collector',
                icon: faRectangleHistoryCircleUser,
            });

            if (Spark.ftpPullAvailable) {
                integrations.links.push({
                    href: '/settings/integrations#/ftp-pull',
                    label: 'FTP Pull',
                    icon: faFolderArrowDown,
                });

                integrations.links.push({
                    href: '/settings/integrations#/ftp-pull-runs',
                    label: 'FTP Pull Runs',
                    icon: faFolderArrowRight,
                });
            }

            if (FEATURES.hubspot_integration) {
                integrations.links.push({
                    href: '/settings/integrations#/hubspot',
                    label: 'HubSpot',
                    icon: faHubspot,
                });
            }

            if (FEATURES.revel_integration) {
                integrations.links.push({
                    href: '/settings/integrations#/revel',
                    label: 'Revel',
                    icon: faCashRegister,
                });
            }

            integrations.links.push({
                href: '/settings/integrations#/shopify',
                label: 'Shopify',
                icon: faShopify,
            });

            if (FEATURES.amplitude_integration) {
                integrations.links.push({
                    href: '/settings/integrations#/amplitude',
                    label: 'Amplitude',
                    icon: faWaveformLines,
                });
            }

            if (FEATURES.shift_to_text) {
                integrations.links.push({
                    href: `/settings/teams/${teamId}#/shift-to-text`,
                    label: 'Shift to Text',
                    icon: faPhoneArrowRight,
                });
            }

            allLinks.push(integrations);

            const billing: SettingsSection = {
                label: 'Billing',
                links: [],
            };

            if (isBillable) {
                billing.links.push({
                    href: `/settings/billing#/billing-details`,
                    label: 'Billing Details',
                    icon: faCreditCard,
                });

                if (teamStatus !== 'closed') {
                    billing.links.push({
                        href: `/settings/billing#/proforma-invoices`,
                        label: 'Upcoming Invoices',
                        icon: faMoneyCheckDollarPen,
                    });
                }

                billing.links.push({
                    href: `/settings/billing#/historical-invoices`,
                    label: 'Historical Invoices',
                    icon: faShoppingCart,
                });
            } else if (parentId) {
                billing.links.push({
                    href: `/settings/billing#/billing-details`,
                    label: 'Billing Details',
                    icon: faCreditCard,
                });
            }

            if (billing.links.length > 0) {
                allLinks.push(billing);
            }
        }

        if (currentOrgUnit.value?.id) {
            const orgUnitSettings: SettingsSection = {
                label: 'OU Settings',
                links: [],
            };

            orgUnitSettings.links.push({
                href: `/settings/teams/${teamId}#/current-org-unit-details`,
                label: 'Details',
                icon: faCircleInfo,
            });

            if (roleOnCurrentOrgUnit === 'admin') {
                orgUnitSettings.links.push({
                    href: `/settings/teams/${teamId}#/current-org-unit-users`,
                    label: 'Users',
                    icon: faUsers,
                });

                orgUnitSettings.links.push({
                    href: `/settings/teams/${teamId}#/current-org-unit-groups`,
                    label: 'Groups',
                    icon: faGrid2Plus,
                });
            }

            orgUnitSettings.links.push({
                href: `/settings/teams/${teamId}#/current-org-unit-collections`,
                label: 'Collections',
                icon: faRectangleHistoryCirclePlus,
            });

            allLinks.push(orgUnitSettings);

            if (currentOrgUnit.value?.is_billable && roleOnCurrentOrgUnit === 'admin') {
                const ouBilling: SettingsSection = {
                    label: 'Billing',
                    links: [
                        {
                            href: `/settings/org-unit-billing#/billing-details`,
                            label: 'Billing Details',
                            icon: faCreditCard,
                        },
                        {
                            href: `/settings/org-unit-billing#/proforma-invoices`,
                            label: 'Upcoming Invoices',
                            icon: faMoneyCheckDollarPen,
                        },
                        {
                            href: `/settings/org-unit-billing#/historical-invoices`,
                            label: 'Historical Invoices',
                            icon: faShoppingCart,
                        },
                    ],
                };
                allLinks.push(ouBilling);
            }
        }

        if (currentGroup.value?.group_id) {
            const groupSettings: SettingsSection = {
                label: 'Group Settings',
                links: [],
            };

            groupSettings.links.push({
                href: `/settings/teams/${teamId}#/group-details`,
                label: 'Details',
                icon: faCircleInfo,
            });

            if (roleOnCurrentGroup === 'admin' || roleOnCurrentGroup === 'owner') {
                groupSettings.links.push({
                    href: `/settings/teams/${teamId}#/group-users`,
                    label: 'Users',
                    icon: faUsers,
                });
            }

            allLinks.push(groupSettings);

            if (currentGroup.value?.is_billable && (roleOnCurrentGroup === 'admin' || roleOnCurrentGroup === 'owner')) {
                const groupBilling: SettingsSection = {
                    label: 'Billing',
                    links: [
                        {
                            href: '/settings/group-billing#/billing-details',
                            label: 'Billing Details',
                            icon: faCreditCard,
                        },
                        {
                            href: '/settings/group-billing#/proforma-invoices',
                            label: 'Upcoming Invoices',
                            icon: faMoneyCheckDollarPen,
                        },
                        {
                            href: '/settings/group-billing#/historical-invoices',
                            label: 'Historical Invoices',
                            icon: faShoppingCart,
                        },
                    ],
                };
                allLinks.push(groupBilling);
            }
        }

        return allLinks;
    });
}
